import { useState, useEffect } from 'react';
import InstallButton from '../../components/InstallButton';
import { getGlobalStats, type GlobalStats } from '../../api/stats.api';
import { useNavigate } from 'react-router-dom';
import PlanDistributionChart from './components/PlanDistributionChart';
import { formatCOP } from '../../utils/currencyFormat';

export default function SuperAdminDashboard() {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [stats, setStats] = useState<GlobalStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    loadStats();

    return () => clearInterval(timer);
  }, []);

  const loadStats = async () => {
    try {
      const data = await getGlobalStats();
      setStats(data);
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getPlanColor = (planName: string) => {
    switch(planName) {
      case 'Enterprise': return 'bg-purple-500';
      case 'Profesional': return 'bg-teal-500';
      case 'Básico': return 'bg-blue-500';
      default: return 'bg-slate-500';
    }
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'ACTIVA': return 'bg-green-100 text-green-700';
      case 'INACTIVA': return 'bg-red-100 text-red-700';
      case 'PRUEBA': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  // Calcular porcentajes para la distribución de planes
  const totalPlans = stats?.planDistribution.reduce((acc, curr) => acc + curr.count, 0) || 1;
  const planDistribution = stats?.planDistribution.map(p => ({
    ...p,
    percentage: Math.round((p.count / totalPlans) * 100),
    color: getPlanColor(p.plan)
  })) || [];

  if (isLoading) {
    return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Cargando métricas...</p>
          </div>
        </div>
    );
  }

  return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">
              Panel de Control Global
            </h1>
            <p className="text-slate-500 mt-1">
              {currentTime.toLocaleDateString('es-ES', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })} - {currentTime.toLocaleTimeString('es-ES')}
            </p>
          </div>


          <div className="flex gap-3 items-center">
            <InstallButton />
            <button className="px-4 py-2 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition flex items-center gap-2 whitespace-nowrap">
              <i className="ri-refresh-line"></i>
              <span className="text-sm font-medium" onClick={loadStats}>Actualizar</span>
            </button>
          </div>
        </div>

        {/* Global Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg transition">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-slate-500 font-medium">Empresas Activas</p>
                <div className="flex items-baseline gap-2 mt-2">
                  <h3 className="text-3xl font-bold text-slate-800">{stats?.activeCompanies || 0}</h3>
                  <span className="text-sm text-slate-400">/ {stats?.totalCompanies} Total</span>
                </div>
              </div>
              <div className="bg-blue-500 w-12 h-12 rounded-xl flex items-center justify-center text-white">
                <i className="ri-building-line text-xl"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg transition">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-slate-500 font-medium">Ingresos Mensuales</p>
                <div className="flex items-baseline gap-2 mt-2">
                  <h3 className="text-3xl font-bold text-slate-800">
                    ${stats?.monthlyRevenue.toLocaleString() || 0}
                  </h3>
                  <span className="text-sm text-slate-400">Estimado</span>
                </div>
              </div>
              <div className="bg-teal-500 w-12 h-12 rounded-xl flex items-center justify-center text-white">
                <i className="ri-money-dollar-circle-line text-xl"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg transition">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-slate-500 font-medium">Suscripciones Activas</p>
                <div className="flex items-baseline gap-2 mt-2">
                  <h3 className="text-3xl font-bold text-slate-800">{stats?.activeSubscriptions || 0}</h3>
                </div>
              </div>
              <div className="bg-purple-500 w-12 h-12 rounded-xl flex items-center justify-center text-white">
                <i className="ri-vip-crown-line text-xl"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg transition">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-slate-500 font-medium">Usuarios Totales</p>
                <div className="flex items-baseline gap-2 mt-2">
                  <h3 className="text-3xl font-bold text-slate-800">{stats?.totalUsers || 0}</h3>
                </div>
              </div>
              <div className="bg-green-500 w-12 h-12 rounded-xl flex items-center justify-center text-white">
                <i className="ri-group-line text-xl"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 gap-6">
          {/* Plan Distribution */}
          <PlanDistributionChart data={planDistribution} />
        </div>

        {/* Recent Companies */}
        <div className="bg-white rounded-2xl border border-slate-200">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-800">Últimas Empresas Registradas</h3>
              <button 
                onClick={() => navigate('/empresas')}
                className="text-sm text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap"
              >
                Ver todas →
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Empresa
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Fecha Registro
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Precio Plan
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {stats?.recentCompanies.map((company) => (
                  <tr key={company.id} className="hover:bg-slate-50 transition">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-bold">
                          {company.name.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium text-slate-800">{company.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-lg text-xs font-medium ${getPlanColor(company.plan)} bg-opacity-20 text-${getPlanColor(company.plan).replace('bg-', '')}`}>
                        {company.plan}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-lg text-xs font-medium ${getStatusColor(company.status)}`}>
                        {company.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {new Date(company.date).toLocaleDateString('es-ES')}
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-bold text-slate-800">
                        {formatCOP(company.revenue)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(!stats?.recentCompanies || stats.recentCompanies.length === 0) && (
              <div className="p-8 text-center text-slate-500">
                No hay empresas recientes para mostrar.
              </div>
            )}
          </div>
        </div>
      </div>
  );
}
